import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Share2, Calendar, Clock, MapPin, Users,
  Crown, CheckCircle2, Map as MapIcon, ChevronRight,
  ShieldAlert, Pencil, UserCircle2, ExternalLink,
} from 'lucide-react';
import { fetchEvent, signupToEvent, cancelEventSignup } from '../../api/move';
import type { MoveEvent, MoveEventAttendee } from '../../api/move';
import { API_BASE } from '../../config';
import { SecureStorage } from '../../storage/SecureStorage';
import RouteMap from '../../components/Move/RouteMap';
import { cancelMoveEventReminder, scheduleOneMoveEventReminder } from '../../notifications/moveEvents';
import styles from '../../styles/Move.module.css';

function formatLongDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString('es-ES', {
      weekday: 'long', day: 'numeric', month: 'long',
    });
  } catch { return iso; }
}

function formatTime(iso: string) {
  try {
    return new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

function durationMinutes(start: string, end?: string | null) {
  if (!end) return null;
  try {
    const mins = Math.round((new Date(end).getTime() - new Date(start).getTime()) / 60000);
    return mins > 0 ? mins : null;
  } catch { return null; }
}

function formatDuration(mins: number) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h <= 0) return `${m} min`;
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
}

function imageUrl(filename?: string | null) {
  if (!filename) return null;
  if (filename.startsWith('http')) return filename;
  return `${API_BASE}/api/move_images/${filename}`;
}

export default function EventDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState<MoveEvent | null>(null);
  const [attendees, setAttendees] = useState<MoveEventAttendee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [showLiability, setShowLiability] = useState(false);
  const [showAttendees, setShowAttendees] = useState(false);
  const [acceptedLiability, setAcceptedLiability] = useState(false);

  const eventId = Number(id);
  const [joined, setJoined] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const u = await SecureStorage.getUser?.();
        if (u?.id) setCurrentUserId(u.id);
      } catch { /* ignore */ }
    })();
  }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchEvent(eventId);
      setEvent(data.event);
      setAttendees(data.attendees || []);
      setJoined(Boolean(data.event.is_joined));
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Error cargando evento');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { if (eventId) load(); }, [eventId, currentUserId]);

  function flashToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 2200);
  }

  async function handleToggle() {
    if (!event) return;
    if (!joined) {
      setAcceptedLiability(false);
      setShowLiability(true);
      return;
    }
    setBusy(true);
    try {
      await cancelEventSignup(eventId);
      await cancelMoveEventReminder(eventId).catch(() => undefined);
      setJoined(false);
      flashToast('Inscripción cancelada');
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.error || 'No se pudo procesar');
    } finally {
      setBusy(false);
    }
  }

  async function confirmSignup() {
    if (!event || !acceptedLiability) return;
    setBusy(true);
    try {
      await signupToEvent(eventId, { accepted_responsibility: true });
      await scheduleOneMoveEventReminder(event).catch(() => undefined);
      setJoined(true);
      setShowLiability(false);
      flashToast('¡Te has apuntado!');
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.error || 'No se pudo procesar');
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className={`move-app ${styles.page}`}><p className={styles.empty}>Cargando…</p></div>;
  if (error || !event) {
    return (
      <div className={`move-app ${styles.page}`}>
        <p className={styles.empty}>{error || 'Evento no encontrado'}</p>
        <div style={{ padding: '0 1.25rem' }}>
          <button className={styles.btnGhost} style={{ width: '100%' }} onClick={() => navigate('/move/events')}>
            <ArrowLeft size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
            Volver
          </button>
        </div>
      </div>
    );
  }

  const img = imageUrl(event.imagen);
  const ocupadas = event.plazas_ocupadas ?? 0;
  const max = event.plazas_max ?? 0;
  const fillPct = max > 0 ? Math.min(100, (ocupadas / max) * 100) : 0;
  const dur = durationMinutes(event.fecha_inicio, event.fecha_fin);
  const isOwner = currentUserId != null && event.creado_por != null && Number(event.creado_por) === Number(currentUserId);

  return (
    <div className={`move-app ${styles.page}`} style={{ paddingTop: 0 }}>
      {/* Hero imagen */}
      <div className={styles.heroImage} style={{ height: 256 }}>
        {img ? <img src={img} alt={event.titulo} /> : <div className={styles.heroFallback} />}
        <div className={styles.floatActions}>
          <button className={styles.floatBtn} onClick={() => navigate('/move/events')} aria-label="Volver">
            <ArrowLeft size={18} />
          </button>
          <div className={styles.floatGroup}>
            {isOwner && (
              <button className={styles.floatBtn} onClick={() => navigate(`/move/events/${eventId}/edit`)} aria-label="Editar evento">
                <Pencil size={18} />
              </button>
            )}
            <button className={styles.floatBtn} onClick={() => flashToast('Enlace copiado')} aria-label="Compartir">
              <Share2 size={18} />
            </button>
          </div>
        </div>
      </div>

      <div className={styles.detailContent}>
        <div className={styles.detailMetaRow}>
          <span className={`${styles.tag} ${styles.tagSky}`}>{event.nivel}</span>
          <span className={`${styles.tag} ${styles.tagViolet}`}>{event.tipo}</span>
          {!!event.is_premium && (
            <span className={styles.tagPremium}>
              <Crown size={11} /> Premium
            </span>
          )}
        </div>

        <h1 className={styles.detailTitle}>{event.titulo}</h1>

        {/* Info rows */}
        <div className={styles.infoRows}>
          <div className={styles.infoRow}>
            <div className={`${styles.infoIcon} ${styles.infoIconPrimary}`}>
              <Calendar size={18} />
            </div>
            <div>
              <p className={styles.infoLabel}>Fecha</p>
              <p className={styles.infoValue}>{formatLongDate(event.fecha_inicio)}</p>
            </div>
          </div>
          <div className={styles.infoRow}>
            <div className={`${styles.infoIcon} ${styles.infoIconAccent}`}>
              <Clock size={18} />
            </div>
            <div>
              <p className={styles.infoLabel}>Hora</p>
              <p className={styles.infoValue}>
                {formatTime(event.fecha_inicio)}
                {dur != null && ` · ${formatDuration(dur)}`}
              </p>
            </div>
          </div>
          {event.ubicacion_nombre && (
            <div className={styles.infoRow}>
              <div className={`${styles.infoIcon} ${styles.infoIconSky}`}>
                <MapPin size={18} />
              </div>
              <div>
                <p className={styles.infoLabel}>Ubicación</p>
                <p className={styles.infoValue}>{event.ubicacion_nombre}</p>
              </div>
            </div>
          )}
          {event.organizador_nombre && (
            <div className={styles.infoRow}>
              <div className={`${styles.infoIcon} ${styles.infoIconPrimary}`}>
                <UserCircle2 size={18} />
              </div>
              <div>
                <p className={styles.infoLabel}>Organiza</p>
                <p className={styles.infoValue}>{event.organizador_nombre}</p>
              </div>
            </div>
          )}
        </div>

        {event.descripcion && (
          <p className={styles.detailDesc} style={{ marginBottom: 18 }}>{event.descripcion}</p>
        )}

        {event.inscripcion_url && (
          <button
            className={styles.btnPrimary}
            type="button"
            onClick={() => window.open(event.inscripcion_url || '', '_blank', 'noopener,noreferrer')}
            style={{
              width: '100%',
              marginBottom: 18,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            <ExternalLink size={16} /> Link de inscripción
          </button>
        )}

        {/* Mapa real con pin del evento */}
        {event.lat != null && event.lng != null && (
          <div style={{ marginBottom: 18 }}>
            <RouteMap
              fallbackPoint={{ lat: Number(event.lat), lng: Number(event.lng) }}
              height={160}
            />
          </div>
        )}

        {/* Ruta vinculada */}
        {event.route_id && (
          <div
            onClick={() => navigate(`/move/routes/${event.route_id}`)}
            style={{
              marginBottom: 18, borderRadius: 16, overflow: 'hidden',
              border: '1px solid hsl(142 76% 45% / 0.2)',
              background: 'hsl(142 76% 45% / 0.05)', cursor: 'pointer',
            }}
          >
            {(event.route_geometry || (event.route_lat != null && event.route_lng != null)) && (
              <RouteMap
                geometry={event.route_geometry}
                fallbackPoint={
                  event.route_lat != null && event.route_lng != null
                    ? { lat: Number(event.route_lat), lng: Number(event.route_lng) }
                    : null
                }
                height={140}
                rounded={false}
                interactive={false}
              />
            )}
            <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 34, height: 34, borderRadius: 10, flexShrink: 0,
                background: 'var(--move-grad-primary)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <MapIcon size={16} color="#fff" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--move-fg)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {event.route_titulo ?? 'Ver ruta'}
                </p>
                {(event.route_distancia_m != null || event.route_desnivel_m != null) && (
                  <p style={{ margin: 0, fontSize: 12, color: 'var(--move-muted-fg)' }}>
                    {event.route_distancia_m != null && `${(event.route_distancia_m / 1000).toFixed(1)} km`}
                    {event.route_desnivel_m != null && ` · +${event.route_desnivel_m} m`}
                    {event.route_duracion_min != null && ` · ${event.route_duracion_min} min`}
                  </p>
                )}
              </div>
              <ChevronRight size={16} color="var(--move-muted-fg)" />
            </div>
          </div>
        )}

        {/* Inscritos */}
        <div className={styles.attendeesCard} style={{ marginBottom: 16 }}>
          <button
            className={styles.attendeesToggle}
            type="button"
            onClick={() => setShowAttendees(open => !open)}
            aria-expanded={showAttendees}
          >
            <span className={styles.attendeesTitle}>
              <Users size={16} color="hsl(0 72% 44%)" />
              Inscritos
            </span>
            <span className={styles.attendeesCount}>
              <strong>{ocupadas}</strong>
              {max > 0 && ` de ${max}`}
              <ChevronRight
                size={16}
                className={`${styles.attendeesChevron} ${showAttendees ? styles.attendeesChevronOpen : ''}`}
              />
            </span>
          </button>

          {showAttendees && (
            attendees.length > 0 ? (
              <ul className={styles.attendeesList}>
                {attendees.map((attendee, index) => (
                  <li key={`${attendee.fullName}-${index}`} className={styles.attendeeNameRow}>
                    <span>{attendee.fullName}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className={styles.attendeesEmpty}>
                Todavía no hay inscritos.
              </p>
            )
          )}

          {max > 0 && (
            <div className={styles.progress} style={{ height: 8 }}>
              <div className={styles.progressFill} style={{ width: `${fillPct}%`, boxShadow: 'none' }} />
            </div>
          )}
        </div>

        {!!event.is_premium && event.precio != null && (
          <div className={styles.priceRow}>
            <span className={styles.priceLabel}>Precio</span>
            <span className={styles.priceValue}>{Number(event.precio).toFixed(2)} €</span>
          </div>
        )}

        <button
          disabled={busy}
          onClick={handleToggle}
          className={`${styles.ctaBig} ${joined ? styles.ctaJoined : ''}`}
        >
          {joined
            ? <><CheckCircle2 size={18} /> Apuntado · Cancelar</>
            : <>Apuntarme al evento</>
          }
        </button>
      </div>

      {showLiability && (
        <div className={styles.sheetBackdrop} role="dialog" aria-modal="true">
          <div className={styles.sheet}>
            <div className={styles.sheetHandle} />
            <div className={styles.sheetHeader}>
              <h2 className={styles.sheetTitle}>Responsabilidad</h2>
              <button
                className={styles.sheetClose}
                type="button"
                onClick={() => setShowLiability(false)}
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>

            <div style={{ display: 'grid', gap: 14 }}>
              <div
                style={{
                  display: 'flex',
                  gap: 12,
                  alignItems: 'flex-start',
                  padding: 14,
                  borderRadius: 16,
                  background: 'hsl(0 72% 44% / 0.08)',
                  border: '1px solid hsl(0 72% 44% / 0.18)',
                }}
              >
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 12,
                    background: 'var(--move-grad-primary)',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <ShieldAlert size={18} />
                </div>
                <p style={{ margin: 0, color: 'var(--move-fg)', fontSize: 13, lineHeight: 1.5 }}>
                  Declaro que participo voluntariamente y que mi estado de salud es adecuado para esta actividad.
                  Entiendo que correr, caminar por montaña o ir en bici puede implicar caídas, lesiones, accidentes
                  o problemas médicos. Me comprometo a usar material adecuado, seguir indicaciones y detenerme si
                  noto síntomas. Acepto asumir los riesgos propios de la actividad, sin perjuicio de los derechos y
                  responsabilidades que legalmente no puedan excluirse.
                </p>
              </div>

              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  fontSize: 13,
                  color: 'var(--move-fg)',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={acceptedLiability}
                  onChange={e => setAcceptedLiability(e.target.checked)}
                  style={{ marginTop: 2, width: 18, height: 18, accentColor: 'hsl(0 72% 44%)' }}
                />
                He leído y acepto la cláusula de responsabilidad para inscribirme en este evento.
              </label>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <button
                  className={styles.btnGhost}
                  type="button"
                  onClick={() => setShowLiability(false)}
                  disabled={busy}
                >
                  Cancelar
                </button>
                <button
                  className={styles.btnPrimary}
                  type="button"
                  onClick={confirmSignup}
                  disabled={busy || !acceptedLiability}
                >
                  Aceptar y apuntarme
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div style={{
          position: 'fixed',
          bottom: 100,
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'var(--move-secondary)',
          color: '#fff',
          padding: '10px 18px',
          borderRadius: 999,
          fontSize: 13,
          fontWeight: 600,
          boxShadow: 'var(--move-shadow-md)',
          zIndex: 100,
        }}>
          {toast}
        </div>
      )}
    </div>
  );
}
